import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Link2, RefreshCw, ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';

export function MFilesConnectionsPage() {
  const { data: tokenData } = useQuery({
    queryKey: ['mfiles-token'],
    queryFn: () => api.get('/api/connections/mfiles/generateToken').then((r) => r.data),
  });

  const { data: objectTypes, isLoading: typesLoading } = useQuery({
    queryKey: ['mfiles-object-types'],
    queryFn: () => api.get('/api/connections/mfiles/getObjectTypes').then((r) => r.data),
  });

  const connectMutation = useMutation({
    mutationFn: () => api.get('/api/connections/mfiles/connect').then((r) => r.data),
    onSuccess: () => toast.success('M-Files connection established'),
    onError: () => toast.error('Failed to connect to M-Files'),
  });

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">M-Files Connections</h1>
        <p className="text-gray-500">Manage your M-Files document management integration</p>
      </div>

      {/* Connection Status */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5" /> Connection Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Badge variant={tokenData ? 'success' : 'secondary'}>
                {tokenData ? 'Connected' : 'Not Connected'}
              </Badge>
              {tokenData?.token && (
                <p className="text-xs text-gray-500 mt-2 font-mono">
                  Token: ••••••••{tokenData.token.slice(-4)}
                </p>
              )}
            </div>
            <Button
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {connectMutation.isPending ? 'Connecting...' : 'Connect'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Object Types */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">M-Files Object Types</CardTitle>
          <CardDescription>Available document classes and object types</CardDescription>
        </CardHeader>
        <CardContent>
          {typesLoading ? (
            <p className="text-gray-500">Loading object types...</p>
          ) : !objectTypes || (Array.isArray(objectTypes) && objectTypes.length === 0) ? (
            <p className="text-gray-500">Connect to M-Files to view object types.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Type</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(Array.isArray(objectTypes) ? objectTypes : []).map((obj: any, i: number) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{obj.Name || obj.name}</TableCell>
                    <TableCell>{obj.ID || obj.id}</TableCell>
                    <TableCell>{obj.Type || obj.type || 'Document'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Invoice Integrations */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invoice Integrations</CardTitle>
          <CardDescription>Upsert invoices from M-Files to accounting platforms</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant="outline" asChild>
              <Link to="/connections/mfiles/xero">
                <ExternalLink className="h-4 w-4 mr-2" /> Upsert to Xero
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/connections/mfiles/myob">
                <ExternalLink className="h-4 w-4 mr-2" /> Upsert to MYOB
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}